// src/components/admin/UserStatistics.tsx
import React, { useState, useEffect } from 'react';
import {
  Users,
  RefreshCw,
  AlertCircle,
  UserPlus,
  Contact,
  Link2,
  Sparkles,
  CreditCard,
  BarChart2,
  Mail,
  Calendar,
  MessageCircle
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import { UserStatistics as UserStatisticsType } from '../../types/admin';

const UserStatistics: React.FC = () => {
  const [stats, setStats] = useState<UserStatisticsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await adminApi.getStatistics();
      setStats(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch statistics');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (isLoading && !stats) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-600">Loading statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-medium text-red-800 mb-1">Error Loading Statistics</h3>
            <p className="text-red-700">{error}</p>
            <button
              onClick={fetchStats}
              className="mt-4 px-4 py-2 bg-red-100 text-red-800 rounded-md hover:bg-red-200 inline-flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const maxSignups = Math.max(...stats.signupsByDay.map((d) => d.count), 1);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">User Statistics</h2>
        <button
          onClick={fetchStats}
          className="p-2 text-gray-600 hover:text-gray-800 border rounded-md hover:bg-gray-50"
          title="Refresh Data"
        >
          <RefreshCw className="h-5 w-5" />
        </button>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center mb-2">
            <Users className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-sm font-medium text-gray-600">Total Users</h3>
          </div>
          <div className="text-2xl font-bold">{stats.overview.totalUsers.toLocaleString()}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center mb-2">
            <UserPlus className="h-5 w-5 text-green-600 mr-2" />
            <h3 className="text-sm font-medium text-gray-600">New (7d)</h3>
          </div>
          <div className="text-2xl font-bold">{stats.overview.newUsersLast7Days.toLocaleString()}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center mb-2">
            <UserPlus className="h-5 w-5 text-emerald-600 mr-2" />
            <h3 className="text-sm font-medium text-gray-600">New (30d)</h3>
          </div>
          <div className="text-2xl font-bold">{stats.overview.newUsersLast30Days.toLocaleString()}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center mb-2">
            <CreditCard className="h-5 w-5 text-purple-600 mr-2" />
            <h3 className="text-sm font-medium text-gray-600">Paid Users</h3>
          </div>
          <div className="text-2xl font-bold">{stats.overview.paidUsers.toLocaleString()}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center mb-2">
            <Contact className="h-5 w-5 text-amber-600 mr-2" />
            <h3 className="text-sm font-medium text-gray-600">Total Contacts</h3>
          </div>
          <div className="text-2xl font-bold">{stats.overview.totalContacts.toLocaleString()}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="flex items-center mb-2">
            <Sparkles className="h-5 w-5 text-indigo-600 mr-2" />
            <h3 className="text-sm font-medium text-gray-600">AI Queries (mo)</h3>
          </div>
          <div className="text-2xl font-bold">{stats.overview.totalAIQueries.toLocaleString()}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Signups chart */}
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <h3 className="text-lg font-medium mb-4 flex items-center">
            <BarChart2 className="h-5 w-5 mr-2 text-blue-600" />
            Signups (Last 14 Days)
          </h3>
          <div className="flex gap-2 h-36">
            {stats.signupsByDay.map((day) => (
              <div key={day.date} className="flex-1 flex flex-col items-center min-w-0">
                <div className="flex-1 w-full flex items-end justify-center">
                  <div
                    className="w-full max-w-4 bg-blue-500 rounded-t mx-auto transition-all hover:bg-blue-600"
                    style={{
                      height: `${Math.max((day.count / maxSignups) * 100, 2)}%`,
                      minHeight: day.count > 0 ? '6px' : '2px'
                    }}
                    title={`${day.date}: ${day.count} signups`}
                  />
                </div>
                <span className="text-[10px] text-gray-500 mt-1 truncate w-full text-center">
                  {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Integrations */}
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <h3 className="text-lg font-medium mb-4 flex items-center">
            <Link2 className="h-5 w-5 mr-2 text-blue-600" />
            Integration Connections
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="flex items-center text-gray-700">
                <Mail className="h-4 w-4 mr-2 text-red-500" />
                Gmail
              </span>
              <span className="font-semibold">{stats.integrations.gmailConnected.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center text-gray-700">
                <Calendar className="h-4 w-4 mr-2 text-blue-500" />
                Calendar
              </span>
              <span className="font-semibold">{stats.integrations.calendarConnected.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center text-gray-700">
                <MessageCircle className="h-4 w-4 mr-2 text-sky-500" />
                Telegram
              </span>
              <span className="font-semibold">{stats.integrations.telegramConnected.toLocaleString()}</span>
            </div>
            <div className="pt-3 border-t">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-800">Any integration</span>
                <span className="font-semibold text-blue-600">{stats.integrations.anyIntegration.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Content & engagement */}
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <h3 className="text-lg font-medium mb-4">Content & Engagement</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.content.totalContacts.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Contacts</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.content.totalGroups.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Groups</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.content.totalTodos.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Todos</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.content.totalReminders.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Reminders</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.content.totalInteractions.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Interactions</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t text-sm text-gray-500">
            <span>{stats.users.withZeroContacts} users with no contacts</span>
            <span className="mx-2">•</span>
            <span>Avg {stats.users.avgContactsPerUser} contacts per active user</span>
          </div>
        </div>

        {/* Subscriptions & AI */}
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <h3 className="text-lg font-medium mb-4">Subscriptions</h3>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Free</span>
              <span className="font-medium">{stats.subscriptions.free}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Medium</span>
              <span className="font-medium text-blue-600">{stats.subscriptions.medium}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Premium</span>
              <span className="font-medium text-purple-600">{stats.subscriptions.premium}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Trialing</span>
              <span className="font-medium">{stats.subscriptions.trialing}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Canceled</span>
              <span className="font-medium text-red-600">{stats.subscriptions.canceled}</span>
            </div>
          </div>
          <div className="pt-4 border-t">
            <h4 className="text-sm font-medium text-gray-600 mb-2">AI Usage (This Month)</h4>
            <div className="flex justify-between text-sm">
              <span>{stats.aiUsage.usersWhoUsedAIThisMonth} users used AI</span>
              <span className="font-medium">{stats.aiUsage.totalQueriesThisMonth.toLocaleString()} queries</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserStatistics;
